import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import DOMPurify from 'dompurify';
import type { Aula } from '@/types/masterclass';

interface SerieDescriptionProps {
  aula: Aula | null;
}

export function SerieDescription({ aula }: SerieDescriptionProps) {
  useLanguage();
  if (!aula) {
    return (
      <div className="text-muted-foreground text-sm">
        {translateInterfaceText("Selecione uma aula para ver a descrição.")}</div>
    );
  }

  // Mock de descrição caso esteja vazia
  const mockDescription = `
    <p>Nesta aula, você vai aprender os fundamentos essenciais para dominar as técnicas e estratégias que vão transformar seus resultados. Vamos abordar desde os conceitos iniciais até a aplicação prática no seu dia a dia.</p>
    
    <h4>Principais tópicos abordados:</h4>
    <ul>
      <li>Como estruturar sua estratégia do zero</li>
      <li>Técnicas comprovadas para maximizar resultados</li>
      <li>Erros comuns que você deve evitar</li>
      <li>Cases de sucesso e exemplos práticos</li>
      <li>Próximos passos para implementação</li>
    </ul>
    
    <p><strong>Ao final desta aula, você será capaz de:</strong> Aplicar imediatamente os conceitos aprendidos e começar a ver resultados concretos no seu negócio.</p>
  `;

  const descriptionToUse = aula.descricao?.trim() || mockDescription;

  // Sanitizar HTML
  const sanitizedHTML = DOMPurify.sanitize(descriptionToUse, {
    ALLOWED_TAGS: ['p', 'ul', 'ol', 'li', 'a', 'strong', 'em', 'br', 'h3', 'h4'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });

  // Adicionar rel="noopener noreferrer" em todos os links
  const finalHTML = sanitizedHTML.replace(
    /<a /g,
    '<a target="_blank" rel="noopener noreferrer" '
  );

  return (
    <div>
      <h2 className="text-xl font-semibold mb-3">{aula.titulo}</h2>
      <div
        className="prose prose-sm max-w-none text-muted-foreground"
        dangerouslySetInnerHTML={{ __html: finalHTML }}
      />

      {aula.objetivos && aula.objetivos.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium mb-2">{translateInterfaceText("O que você vai aprender:")}</h3>
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
            {aula.objetivos.map((obj, index) => (
              <li key={index}>{obj}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
